import useBasketStore from '@/context/BasketStore'
import { Loader, ChevronRight, ArrowLeft, ArrowUpRight, Menu, X, Plus } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import RegionSelectorModal from '../RegionSelectorModal'
import useRegionStore from '@/context/RegionStore'
import useCountryDetailsStore from '@/context/CountryDetailsStore'
import { buildRegionUrl, getRegionFromUrl } from '@/utils/regionDetection'
import Button, { NavButton } from '../Button'
import NotificationBar from './NotificationBar'
import FormLightbox from '@/modules/forms/components/FormLightbox'
import { fetchProductCollection } from '@/services/api'
import { ProductCollection } from '@/modules/collections/types/collection.types'
import { useCurrency } from '@/hooks/useCurrency'
import NavLogo from './NavLogo'
import NavBasketButton from './NavBasketButton'
import NavProductCard from './NavProductCard'
import NavSupportActions from './NavSupportActions'

interface NavProps {
  isVisible: boolean
  onHeightChange: (height: number) => void
  className?: string
  disableScrollAnimation?: boolean
}

type DesktopDropdown = 'support' | 'headphones' | 'repair'
type SupportForm = 'retailer' | 'investor'

const HEADPHONES_COLLECTION_CODE = 'F39Jxkd4q'

interface MobileSubMenuHeaderProps {
  title: string
  onBack: () => void
  className?: string
}

const MobileSubMenuHeader = ({ title, onBack, className = 'px-1 py-2' }: MobileSubMenuHeaderProps) => (
  <div className={`${className} flex items-center justify-between`}>
    <button
      onClick={onBack}
      className="text-gray-700 p-1 rounded-full border border-gray-200 bg-white shadow-sm"
      aria-label="Back to menu"
    >
      <ArrowLeft className="w-5 h-5" />
    </button>
    <span className="text-gray-900 font-headline text-xl text-white">{title}</span>
    <div className="w-8" />
  </div>
)

const Nav = ({ isVisible, onHeightChange, className = '', disableScrollAnimation = false }: NavProps) => {
  const { basketOpen, setBasketOpen, itemCount } = useBasketStore()
  const navRef = useRef<HTMLElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const supportButtonRef = useRef<HTMLDivElement>(null)
  const headphonesButtonRef = useRef<HTMLDivElement>(null)
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState<DesktopDropdown | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [activeForm, setActiveForm] = useState<SupportForm | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false)
  const [mobileSubMenu, setMobileSubMenu] = useState<DesktopDropdown | null>(null)
  const [pendingDesktopNavigation, setPendingDesktopNavigation] = useState<string | null>(null)
  const [headphonesCollection, setHeadphonesCollection] = useState<ProductCollection | null>(null)
  const [headphonesCollectionRegion, setHeadphonesCollectionRegion] = useState<string | null>(null)
  const headphonesCollectionFetchedAt = useRef(0)
  const headphonesCollectionAttempt = useRef<{ at: number; region: string } | null>(null)
  const [isHeadphonesLoading, setIsHeadphonesLoading] = useState(false)
  const lastHoverOpenAtRef = useRef<number>(0)
  const hoverSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { chosenRegion, detectedRegion, isDetectingRegion } = useRegionStore()
  const { getRegionInfo } = useCountryDetailsStore()
  const { formatPrice } = useCurrency()
  const location = useLocation()
  const navigate = useNavigate()

  const routeRegion = getRegionFromUrl(location.pathname)
  const region = routeRegion || chosenRegion || 'gb'
  const resolvedRegion = routeRegion || chosenRegion || detectedRegion || 'gb'
  const regionCode = resolvedRegion.toLowerCase()
  const isProductRoute = location.pathname.includes('/products')
  const isRepairRoute = location.pathname.includes('/repair')
  const isTrackRepairRoute = /\/track\/REPAIR-/.test(location.pathname)
  const isSustainabilityRoute = location.pathname.includes('/sustainability')
  const isLegalRoute = location.pathname.includes('/legal')
  const topOffsetClass = isProductRoute ? 'top-0' : 'top-[34px]'
  const navBgColor = isRepairRoute || isTrackRepairRoute ? 'bg-kibuBlue' : isSustainabilityRoute ? 'bg-green' : isLegalRoute ? 'bg-darkGrey' : 'bg-primary'
  const headphonesBasePath = buildRegionUrl(resolvedRegion, 'products/wired-headphones-1')
  const repairPartsPath = buildRegionUrl(resolvedRegion, 'repair/parts')
  const contactPath = '/contact/'
  const investorPath = '/investor/'
  const countryInfo = getRegionInfo(chosenRegion || detectedRegion)

  const featuredHeadphones = headphonesCollection?.products?.slice(0, 3) || []
  const firstCollectionTitle = headphonesCollection?.products?.[0]?.title || 'All Headphones'
  const allHeadphones = headphonesCollection?.products || []
  const mobileSubMenuTranslateX = mobileSubMenu ? '-100%' : '0'

  const cancelHoverSwitch = () => {
    if (hoverSwitchTimerRef.current) {
      clearTimeout(hoverSwitchTimerRef.current)
      hoverSwitchTimerRef.current = null
    }
    lastHoverOpenAtRef.current = Date.now()
  }

  const openDropdown = (dropdown: DesktopDropdown, source: 'hover' | 'click' = 'click') => {
    cancelHoverSwitch()
    setPendingDesktopNavigation(null)

    if (source === 'hover') {
      const isSwitching = activeDesktopDropdown && activeDesktopDropdown !== dropdown
      const leftButtonRecently = Date.now() - lastHoverOpenAtRef.current < 1000

      if (isSwitching && leftButtonRecently) {
        hoverSwitchTimerRef.current = setTimeout(() => {
          setIsClosing(false)
          setActiveDesktopDropdown(dropdown)
        }, 200)
        return
      }
    }

    if (activeDesktopDropdown !== dropdown) {
      setIsClosing(false)
      setActiveDesktopDropdown(dropdown)
    }
  }

  const closeDropdown = () => {
    if (activeDesktopDropdown && !isClosing) {
      setIsClosing(true)
    }
  }

  const closeMobileMenu = () => {
    if (isMobileMenuOpen && !isMobileMenuClosing) {
      setIsMobileMenuClosing(true)
    }
  }

  const handleDesktopDropdownButtonClick = (dropdown: DesktopDropdown, isActive: boolean) => {
    if (isActive) {
      setPendingDesktopNavigation(dropdown === 'headphones' ? headphonesBasePath : contactPath)
      closeDropdown()
      return
    }

    openDropdown(dropdown, 'click')
  }

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu()
      return
    }

    setIsMobileMenuClosing(false)
    setIsMobileMenuOpen(true)
  }

  const handleSupportFormOpen = (form: SupportForm, onAfterOpen: () => void) => {
    setActiveForm(form)
    onAfterOpen()
  }

  useEffect(() => {
    if (!navRef.current) {
      return
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height)
      }
    })

    resizeObserver.observe(navRef.current)
    return () => resizeObserver.disconnect()
  }, [onHeightChange])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        supportButtonRef.current && !supportButtonRef.current.contains(target) &&
        headphonesButtonRef.current && !headphonesButtonRef.current.contains(target)
      ) {
        if (activeDesktopDropdown && !isClosing) {
          setIsClosing(true)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [activeDesktopDropdown, isClosing])

  useEffect(() => {
    const shouldLoadHeadphones = activeDesktopDropdown === 'headphones' || mobileSubMenu === 'headphones'
    const collectionIsCurrent = headphonesCollection && headphonesCollectionRegion === regionCode && Date.now() - headphonesCollectionFetchedAt.current < 60000

    if (!shouldLoadHeadphones || isHeadphonesLoading || collectionIsCurrent) {
      return
    }
    const attempt = headphonesCollectionAttempt.current
    if (attempt?.region === regionCode && Date.now() - attempt.at < 30000) return

    const loadHeadphonesCollection = async () => {
      try {
        headphonesCollectionAttempt.current = { at: Date.now(), region: regionCode }
        setIsHeadphonesLoading(true)
        setHeadphonesCollection(null)
        const data = await fetchProductCollection({
          collectionCode: HEADPHONES_COLLECTION_CODE,
          country_code: regionCode,
        })
        headphonesCollectionFetchedAt.current = Date.now()
        setHeadphonesCollection(data)
        setHeadphonesCollectionRegion(regionCode)
      } catch (error) {
        console.error('Failed to fetch headphones collection:', error)
      } finally {
        setIsHeadphonesLoading(false)
      }
    }

    loadHeadphonesCollection()
  }, [activeDesktopDropdown, mobileSubMenu, regionCode, headphonesCollection, headphonesCollectionRegion, isHeadphonesLoading])

  useEffect(() => {
    if (disableScrollAnimation || isVisible) {
      return
    }

    closeDropdown()
  }, [isVisible, disableScrollAnimation, activeDesktopDropdown, isClosing])

  const handleDesktopDropdownAnimationEnd = () => {
    if (!isClosing) {
      return
    }

    if (pendingDesktopNavigation) {
      navigate(pendingDesktopNavigation)
      setPendingDesktopNavigation(null)
    }
    setActiveDesktopDropdown(null)
    setIsClosing(false)
  }

  const handleMobileMenuAnimationEnd = () => {
    if (!isMobileMenuClosing) {
      return
    }

    setIsMobileMenuOpen(false)
    setIsMobileMenuClosing(false)
    setMobileSubMenu(null)
  }

  return (
    <>
      <div className={`fixed ${topOffsetClass} left-0 z-30 md:pl-5 pl-5 h-[70px] hidden md:flex items-center`}>
        <NavLogo fill="#FF5C00" />
      </div>

      <div className={`fixed ${topOffsetClass} right-0 z-30 md:pr-5 pr-5 h-[70px] hidden md:flex items-center gap-6`}>
        {!isProductRoute && (
          <Button
            to={`/${region}/products/wired-headphones-1/`}
            text="Shop Now"
            variant="solid-yellow"
          />
        )}
        <NavBasketButton itemCount={itemCount} onClick={() => setBasketOpen(!basketOpen)} isOrange />
      </div>

      <nav
        ref={navRef}
        className={`site-navigation fixed left-0 ${navBgColor} md:pl-5 md:pr-5 pl-5 pr-5 h-[84px] md:h-[104px] w-full z-30 ${!disableScrollAnimation ? 'transition-transform duration-700 will-change-transform' : ''
          } ${!disableScrollAnimation ? (isVisible ? 'translate-y-0' : '-translate-y-full') : 'translate-y-0'} ${className}`}
      >
        <NotificationBar />

        <div className={`fixed top-[34px] left-0 ${navBgColor} md:pl-5 md:pr-5 pl-5 pr-5 w-full h-[50px] md:h-[70px] z-30`}>
          <div className="mx-auto flex justify-between items-center h-full w-full">
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="text-white p-1 mt-[1px]"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
              <NavLogo fill="white" />
            </div>

            <div className="hidden md:block">
              <NavLogo fill="white" />
            </div>

            <div className="md:hidden">
              <NavBasketButton itemCount={itemCount} onClick={() => setBasketOpen(!basketOpen)} />
            </div>

            <div className="hidden md:flex items-center space-x-6">
              <div className="relative" ref={headphonesButtonRef}>
                <NavButton
                  onClick={() => handleDesktopDropdownButtonClick('headphones', activeDesktopDropdown === 'headphones')}
                >
                  <span className="relative -top-px">Headphones</span>
                  <Plus className={`size-4 relative top-[0.5px] transition-transform duration-200 ${activeDesktopDropdown === 'headphones' ? 'rotate-[225deg]' : ''}`} />
                </NavButton>
              </div>

              <NavButton
                to={repairPartsPath}
                className={activeDesktopDropdown === 'repair' ? 'rotate-[225deg]' : ''}
              >
                <span className="relative -top-px">Repair</span>
              </NavButton>

              <div className="relative" ref={supportButtonRef}>
                <NavButton
                  onClick={() => handleDesktopDropdownButtonClick('support', activeDesktopDropdown === 'support')}
                >
                  <span className="relative -top-px">Support</span>
                  <Plus className={`size-4 relative top-[0.5px] transition-transform duration-200 ${activeDesktopDropdown === 'support' ? 'rotate-[225deg]' : ''}`} />
                </NavButton>
              </div>

              <button
                onClick={() => setIsRegionModalOpen(true)}
                disabled={isDetectingRegion}
                className="w-10 h-10 rounded-full bg-white/10 md:hover:bg-white/20 transition-colors flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Change region"
              >
                {isDetectingRegion ? <Loader className="w-4 h-4 animate-spin" /> : countryInfo?.flag || '🌍'}
              </button>
              <div onMouseEnter={closeDropdown}>
                <NavBasketButton itemCount={itemCount} onClick={() => setBasketOpen(!basketOpen)} />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {activeDesktopDropdown && (
        <div
          ref={dropdownRef}
          className={`fixed left-0 right-0 z-20 ${!disableScrollAnimation ? 'transition-[top] duration-700' : ''
            } ${!disableScrollAnimation && !isVisible ? 'top-0' : 'top-[104px]'} ${isClosing ? 'animate-slideUp' : 'animate-slideDown ease-hypereaseout'}`}
          onAnimationEnd={handleDesktopDropdownAnimationEnd}
        >
          <div className="bg-orange rounded-b-3xl shadow-xl overflow-hidden border-t">
            <div className="max-w-7xl mx-auto px-5 md:px-5 py-4 bg-orange">
              {activeDesktopDropdown === 'support' && (
                <NavSupportActions
                  variant="desktop"
                  contactUrl={contactPath}
                  onContactClick={closeDropdown}
                  onRetailerClick={() => handleSupportFormOpen('retailer', closeDropdown)}
                  onInvestorClick={() => { closeDropdown(); navigate(investorPath) }}
                />
              )}

              {activeDesktopDropdown === 'headphones' && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {isHeadphonesLoading && (
                    <div className="md:col-span-3 px-6 py-5 rounded-2xl bg-grey text-gray-600 flex items-center justify-center">
                      Loading headphones...
                    </div>
                  )}

                  {!isHeadphonesLoading && featuredHeadphones.map((product) => (
                    <NavProductCard
                      key={product.productItemId}
                      variant="desktop"
                      product={product}
                      to={{
                        pathname: headphonesBasePath,
                        search: `?variant=${encodeURIComponent(product.productItemId)}`,
                      }}
                      onClick={closeDropdown}
                      formatPrice={formatPrice}
                    />
                  ))}

                  <Link
                    to={headphonesBasePath}
                    className="block px-6 py-5 rounded-2xl bg-orange-600 text-white md:hover:bg-orange-600/90 transition-colors duration-300 group min-h-[170px] relative pb-12"
                    onClick={closeDropdown}
                  >
                    <h3 className="font-headline text-xl mb-1.5 md:group-hover:scale-[102%] transition-transform duration-300">
                      All Headphones
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed">Discover the full range.</p>
                    <ArrowUpRight className="size-8 absolute right-6 bottom-5 text-white/90 md:group-hover:scale-[110%] transition-transform duration-300" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isMobileMenuOpen && (
        <div
          className={`fixed inset-x-0 top-[83px] bottom-0 z-20 md:hidden ${isMobileMenuClosing ? 'animate-slideOutLeft' : 'animate-slideInLeft'}`}
          onAnimationEnd={handleMobileMenuAnimationEnd}
        >
          <div className="bg-white h-full flex flex-col overflow-hidden min-h-0">
            <div
              className="flex-1 flex min-h-0 transition-transform duration-300 ease-kibushopdefault"
              style={{ transform: `translateX(${mobileSubMenuTranslateX})` }}
            >
              <div className="w-full flex-shrink-0 flex flex-col p-5 gap-4 overflow-y-auto min-h-0 bg-orange border-t">
                <div className="flex flex-col gap-3">
                  <Link
                    to=""
                    onClick={closeMobileMenu}
                    className="flex items-center justify-between w-full rounded-2xl px-6 py-5 font-headline text-xl transition-colors bg-grey text-gray-700 active:bg-gray-200"
                  >
                    Home
                  </Link>
                  <button
                    onClick={() => setMobileSubMenu('headphones')}
                    className="flex items-center justify-between w-full rounded-2xl px-6 py-5 font-headline text-xl transition-colors bg-grey text-gray-700 active:bg-gray-200"
                  >
                    Headphones
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  <Link
                    to={repairPartsPath}
                    onClick={closeMobileMenu}
                    className="flex items-center justify-between w-full rounded-2xl px-6 py-5 font-headline text-xl transition-colors bg-grey text-gray-700 active:bg-gray-200"
                  >
                    Repair
                  </Link>

                  <button
                    onClick={() => setMobileSubMenu('support')}
                    className="flex items-center justify-between w-full rounded-2xl px-6 py-5 font-headline text-xl transition-colors bg-grey text-gray-700 active:bg-gray-200"
                  >
                    Support
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>

                <div className="mt-auto p-6 border-t border-gray-100 flex items-center">
                  <button
                    onClick={() => setIsRegionModalOpen(true)}
                    disabled={isDetectingRegion}
                    className="flex items-center gap-3 text-white font-headline"
                  >
                    <span className="size-10 rounded-full bg-grey flex items-center justify-center text-lg">
                      {isDetectingRegion ? <Loader className="w-4 h-4 animate-spin" /> : countryInfo?.flag || '🌍'}
                    </span>
                    Change region
                  </button>
                </div>
              </div>

              <div className="w-full flex-shrink-0 flex flex-col overflow-y-auto min-h-0 bg-orange border-t">
                {mobileSubMenu === 'headphones' && (
                  <>
                    <MobileSubMenuHeader title="Headphones" onBack={() => setMobileSubMenu(null)} className="sticky top-0 p-5 z-30 bg-orange" />

                    <div className="grid grid-cols-2 gap-3 px-5 pt-1 pb-8">
                      <Link
                        to={headphonesBasePath}
                        className="col-span-2 block rounded-2xl bg-orange-600 text-white px-6 py-5 transition-colors active:bg-orange/90 relative pb-12"
                        onClick={closeMobileMenu}
                      >
                        <h3 className="font-headline text-xl mb-1">All Headphones</h3>
                        <p className="text-sm text-white/80">Shop the full range, starting with {firstCollectionTitle}</p>
                        <ArrowUpRight className="size-8 absolute right-6 bottom-5 text-white/90" />
                      </Link>

                      {isHeadphonesLoading && (
                        <div className="col-span-2 rounded-2xl bg-grey px-6 py-5 text-sm text-gray-600">Loading headphones...</div>
                      )}

                      {!isHeadphonesLoading && allHeadphones.map((product) => (
                        <NavProductCard
                          key={product.productItemId}
                          variant="mobile"
                          product={product}
                          to={{
                            pathname: headphonesBasePath,
                            search: `?variant=${encodeURIComponent(product.productItemId)}`,
                          }}
                          onClick={closeMobileMenu}
                          formatPrice={formatPrice}
                        />
                      ))}
                    </div>
                  </>
                )}

                {mobileSubMenu === 'support' && (
                  <div className="p-5 gap-4 flex flex-col">
                    <MobileSubMenuHeader title="Support" onBack={() => setMobileSubMenu(null)} />
                    <NavSupportActions
                      variant="mobile"
                      contactUrl={contactPath}
                      onContactClick={closeMobileMenu}
                      onRetailerClick={() => handleSupportFormOpen('retailer', closeMobileMenu)}
                      onInvestorClick={() => { closeMobileMenu(); navigate(investorPath) }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isRegionModalOpen && <RegionSelectorModal onClose={() => setIsRegionModalOpen(false)} />}

      {activeForm === 'retailer' && (
        <FormLightbox
          isOpen={activeForm === 'retailer'}
          onClose={() => setActiveForm(null)}
          formId="distributor_enquiry"
          formTitle="Become a Kibu Retailer"
          formDescription="Fill out the form below and our partnerships team will get in touch with you soon."
          onSuccess={(submissionId: string) => {
            console.log('Form submitted successfully:', submissionId)
          }}
          metadata={{
            source: 'nav_support_dropdown',
          }}
        />
      )}

    </>
  )
}

export default Nav
